import { ContentCopyOutlined } from '@mui/icons-material';
import { Box, styled, Typography } from '@mui/material';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import Button from '@/components/Buttons/Button';
import DateText from '@/components/DateText';
import { DateTextVariants } from '@/components/DateText/useDateText';
import { ApiKeyOutput } from '@/slices/apiKeyListSlice';
import copyToClipboard from '@/utils/copyToClipboard';

import ConfirmDialog from './ConfirmDialog';
import useApiKeyTable from './useApiKeyTable';

const StyledTableCell = styled(TableCell)(() => ({
  paddingRight: 30,
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14
  }
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  height: '50px',
  paddingRight: 1,
  borderBottom: `0.5px solid ${theme.palette.info.light}`,
  '&:last-child tr': { border: 0, borderBottom: 0 }
}));

const StyledHeaderRow = styled(TableRow)(({ theme }) => ({
  height: '50px',
  borderBottom: `1px solid ${theme.palette.text.primary}`
}));

const emptyMessage = (
  <Box sx={{ display: 'flex', width: '100%' }}>
    <Typography
      align='center'
      sx={{ width: '100%', my: 5 }}
    >
      No token generated yet.
    </Typography>
  </Box>
);

const ApiKeyTable = ({ apiKeys }: { apiKeys: ApiKeyOutput[] }) => {
  const { open, setOpen, openConfirmModal, deleteKey } = useApiKeyTable();

  return (
    <Box
      sx={() => ({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%'
      })}
    >
      <ConfirmDialog
        deleteCallback={deleteKey}
        open={open}
        setOpen={setOpen}
      />
      {apiKeys.length ? (
        <TableContainer component={Paper}>
          <Table
            aria-label='api keys table'
            size='small'
            sx={{ minWidth: 600 }}
          >
            <TableHead>
              <StyledHeaderRow>
                <StyledTableCell>
                  <Typography variant='body2'>Name</Typography>
                </StyledTableCell>
                <StyledTableCell>
                  <Typography variant='body2'>Access Token</Typography>
                </StyledTableCell>
                <StyledTableCell>
                  <Typography variant='body2'>Creation Date</Typography>
                </StyledTableCell>
                <StyledTableCell>
                  <Typography variant='body2'>Last Used</Typography>
                </StyledTableCell>
                <StyledTableCell />
              </StyledHeaderRow>
            </TableHead>
            <TableBody>
              {[...apiKeys]
                .sort((a, b) => b.creationDate.localeCompare(a.creationDate))
                .map(apiKey => (
                  <StyledTableRow
                    key={apiKey.label}
                    sx={{ '&:last-child': { borderBottom: 0 } }}
                  >
                    <StyledTableCell
                      scope='row'
                      sx={{ width: '25%' }}
                    >
                      <Typography
                        sx={{ maxWidth: '200px', overflow: 'hidden' }}
                        variant='body2'
                      >
                        {apiKey.label}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell sx={{ width: '25%' }}>
                      {apiKey.prefixedRawApiKey && (
                        <Box
                          sx={{
                            display: 'flex',
                            maxWidth: 210,
                            overflowX: 'auto',
                            overflowY: 'hidden'
                          }}
                        >
                          <Typography
                            mr={0.5}
                            variant='body2'
                          >
                            {`${apiKey.prefixedRawApiKey.substring(0, 9)}**********`}
                          </Typography>
                          <Button
                            size='small'
                            startIcon={<ContentCopyOutlined />}
                            variant='text'
                            onClick={() =>
                              copyToClipboard(
                                apiKey.prefixedRawApiKey ?? '',
                                'Api key copied to clipboard!'
                              )
                            }
                          >
                            Copy
                          </Button>
                        </Box>
                      )}
                      {!apiKey.prefixedRawApiKey && (
                        <Typography
                          sx={{ maxWidth: '200px', overflowX: 'auto', overflowY: 'hidden' }}
                          variant='body2'
                        >
                          {`**********${apiKey.lastThreeCharacters}`}
                        </Typography>
                      )}
                    </StyledTableCell>
                    <StyledTableCell sx={{ width: '15%' }}>
                      <DateText
                        dateString={apiKey.creationDate}
                        variant={DateTextVariants.FULL}
                      />
                    </StyledTableCell>
                    <StyledTableCell sx={{ width: '30%' }}>
                      <DateText
                        dateString={apiKey.lastUsed ?? '---'}
                        variant={DateTextVariants.WITH_TIME}
                      />
                    </StyledTableCell>
                    <StyledTableCell sx={{ width: '15%' }}>
                      <Button
                        color='secondary'
                        size='small'
                        variant='text'
                        onClick={() => {
                          openConfirmModal(apiKey);
                        }}
                      >
                        Remove
                      </Button>
                    </StyledTableCell>
                  </StyledTableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        emptyMessage
      )}
    </Box>
  );
};

export default ApiKeyTable;
